import type { Nullish } from '@shared/types/helpers';
import type { Adres } from '@shared/types/other';
import { tryFormatDate } from './dates';

export function parseAddressAsString(
  { huisnummer, huisnummertoevoeging, plaats, postcode, straat }: Nullish<Adres>,
  style: 'short' | 'full' = 'full',
) {
  let address = `${straat ?? ''} ${huisnummer ?? ''}${huisnummertoevoeging ?? ''}`.trim();

  if (style === 'full') {
    address += `${address ? ',' : ''} ${postcode ?? ''} ${plaats ?? ''}`;
  }

  return address.trim();
}

export function parseArrayAsEnumerationString(
  array: (string | number | Date)[],
  lastDelimiter = 'en',
) {
  const mappedArray = array.map((item) => (item instanceof Date ? tryFormatDate(item) : item));

  return [
    mappedArray.toSpliced(-1, 1).join(', '),
    mappedArray.length > 1 ? ` ${lastDelimiter} ` : '',
    mappedArray.at(-1),
  ].join('');
}

/**
 * Parses a string as a slug.
 * Removes special characters, converts to lowercase, trims whitespace,
 * replaces spaces and underscores with hyphens,
 * and removes leading and trailing hyphens.
 *
 * @param {string} string - The string to parse.
 * @returns {string} - The parsed string as a slug.
 */
export const parseStringAsSlug = (string: string) =>
  string
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Parse a Dutch postal code by inserting a space.
 * Assumes the input is a valid Dutch postal code without space, i.e., '1234AB'.
 *
 * @param {string} postcode - The Dutch postcode.
 * @returns {string} The formatted postcode, i.e., '1234 AB'.
 */
export const parsePostcode = (postcode: string): string => {
  if (postcode.length === 6) {
    const numberPart = postcode.slice(0, 4);
    const letterPart = postcode.slice(4);
    return `${numberPart} ${letterPart.toUpperCase()}`;
  }

  return postcode;
};

/**
 * Decodes HTML entities in the given input.
 *
 * @param {string} input - The input string possibly containing HTML entities.
 * @return {string} - The decoded string without HTML entities.
 *  @example
 *  ```typescript
 * const encodedString = '&lt;p&gt;Hello, world!&lt;/p&gt;';
 * const decodedString = htmlDecode(encodedString);

 *  console.log(decodedString); // <p>Hello, world!</p>
 * ```
 */
export function htmlDecode(input: string) {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
}

export function base64ToBytes(base64: string) {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}
